// 261005 F36 — chọn trạng thái "đang chạy" từ danh sách job của BE (`workbench.jobs.running`) — hàm THUẦN, KHÔNG import antd / react.
//
// Vì sao: state cục bộ (`useBgJob`, `useScriptJob`, `bgJobId` trong AdaptationLocalize…) mất khi tải lại trang / đổi chặng (unmount),
// kéo theo mất tiến độ "đã tải i/N", mất toast xong, và nút không còn khoá. Nguồn sự thật = BE; hook cục bộ chỉ "nhận nuôi" job BE báo đang chạy.
import type { WorkbenchFailedJob, WorkbenchJobKind, WorkbenchRunningJob, WorkbenchStageKey } from '../../../types/adaptationWorkbench';
import { friendlyJobNote } from './jobErrorText';
import { REJUDGE_LABEL, isRejudgeJob } from './rejudgeView';

/** Mirror `SPLIT_PHASES` của BE (`utils/adaptationWorkbench.ts`): job `FILM_BUILD_OUTLINE` ở các phase này ⇒ kind `split`. */
export const SPLIT_JOB_PHASES: ReadonlySet<string> = new Set(['episodes', 'repair', 'review-gate']);
export const GATE_JOB_PHASE = 'review-gate';

type RunningLike = Pick<WorkbenchRunningJob, 'jobId' | 'kind'> & { phase?: string };
type FailedLike = Pick<WorkbenchFailedJob, 'jobId' | 'kind'> & { phase?: string; note?: string };

/** Job nền để `useBgJob` nhận nuôi: nạp / quét / chia tập (phase episodes|repair) — KHÔNG gồm chấm lại cổng (thuộc thẻ Cổng AI) hay `FILM_SPLIT_TEXT` (không phase, của trợ lý). */
export const pickBgCandidate = (running: ReadonlyArray<RunningLike> | null | undefined): { key: 'crawl' | 'scan' | 'episodes'; id: string } | null => {
  const list = running ?? [];
  const crawl = list.find((j) => j.kind === 'crawl');
  if (crawl) return { key: 'crawl', id: crawl.jobId };
  const scan = list.find((j) => j.kind === 'scan');
  if (scan) return { key: 'scan', id: scan.jobId };
  const split = list.find((j) => j.kind === 'split' && (j.phase === 'episodes' || j.phase === 'repair'));
  return split ? { key: 'episodes', id: split.jobId } : null;
};

/** Job viết kịch bản (`kind: write`; sửa/nối tập qua trợ lý là `other`, đọc cả mùa là `season_reader` — không tính). */
export const pickScriptCandidate = (running: ReadonlyArray<RunningLike> | null | undefined): string | null =>
  (running ?? []).find((j) => j.kind === 'write')?.jobId ?? null;

/** Job theo loại; `gate` = chỉ chấm lại cổng (`phase review-gate`), `notGate` = bỏ chấm lại cổng, mặc định = mọi phase. */
export const pickJobId = (running: ReadonlyArray<RunningLike> | null | undefined, kind: WorkbenchJobKind, phase: 'gate' | 'notGate' | 'any' = 'any'): string | null =>
  (running ?? []).find((j) => j.kind === kind
    && (phase === 'any' || (phase === 'gate') === (j.phase === GATE_JOB_PHASE)))?.jobId ?? null;

/**
 * Có job nào đang khoá CHƯƠNG THÔ không — khớp `LOCKING_TYPES` của BE (`rawChapterLock.ts`: nạp, quét, dựng khung xương / chia tập (mọi phase
 * của FILM_BUILD_OUTLINE), bản địa hoá). `FILM_SPLIT_TEXT` (không phase) và viết kịch bản KHÔNG khoá.
 */
export const rawChapterLocked = (running: ReadonlyArray<RunningLike> | null | undefined): boolean =>
  (running ?? []).some((j) => j.kind === 'crawl' || j.kind === 'scan' || j.kind === 'skeleton' || j.kind === 'localize' || j.kind === 'overview'   // 261015 P4c-1: tạo tổng quan = FILM_BUILD_OUTLINE phase 'overview' (BE khoá chương thô)
    || (j.kind === 'split' && !!j.phase && SPLIT_JOB_PHASES.has(j.phase)));

// ─── Job vừa kết thúc ─────────────────────────────────────────────────────

export interface EndedJob { jobId: string; kind: WorkbenchJobKind; phase?: string; outcome: 'done' | 'failed'; note: string }

/** Job có ở lần đo trước mà lần này không còn ⇒ vừa kết thúc; có trong `failed` (cửa sổ 180′) ⇒ lỗi, ngược lại coi là xong. */
export const endedJobs = (
  prev: ReadonlyArray<RunningLike> | null | undefined,
  now: { running: ReadonlyArray<RunningLike>; failed: ReadonlyArray<FailedLike> } | null | undefined,
): EndedJob[] => {
  if (!prev?.length || !now) return [];
  const still = new Set(now.running.map((j) => j.jobId));
  return prev.filter((j) => !still.has(j.jobId)).map((j) => {
    const f = now.failed.find((x) => x.jobId === j.jobId);
    return { jobId: j.jobId, kind: j.kind, ...(j.phase ? { phase: j.phase } : {}), outcome: f ? 'failed' as const : 'done' as const, note: f ? friendlyJobNote(f.note) : '' };
  });
};

/**
 * Báo "xong/lỗi" ở vỏ chỉ khi KHÔNG có chủ nào đang mount để tự báo: nạp/quét/chia tập/viết (chủ = hook ở vỏ, nhận nuôi job) ⇒ không;
 * `other`/`split` không phase (trợ lý) ⇒ không; đọc cả mùa ⇒ chủ ở ⑤; bản địa hoá + chấm lại cổng ⇒ chủ ở ③ (cổng chia tập: ④);
 * dựng khung xương ⇒ không có chủ ⇒ luôn báo; tạo tổng quan ⇒ chủ = `SkeletonStage` (hook `useOverviewJob`, tự báo kèm tóm tắt) nên chỉ báo khi người đã rời ②.
 */
export const watcherShouldToast = (j: Pick<EndedJob, 'kind' | 'phase'>, currentStage: WorkbenchStageKey): boolean => {
  switch (j.kind) {
    case 'crawl': case 'scan': case 'write': return false;
    // 261023 E3-P3b2: «Chấm lại bằng AI» là `other` nhưng KHÔNG có chủ mount lâu dài (nút có thể đã unmount khi người đổi chặng) ⇒ vỏ báo. `other` khác (trợ lý) ⇒ chủ tự báo.
    case 'other': return isRejudgeJob(j);
    case 'split':
      if (j.phase === GATE_JOB_PHASE) return currentStage !== 'split';
      return false;
    case 'season_reader': return currentStage !== 'script';
    case 'localize': return currentStage !== 'localize';
    case 'skeleton': return true;
    case 'overview': return currentStage !== 'skeleton';
    default: return false;
  }
};

const DONE_LABEL: Record<WorkbenchJobKind, string> = {
  crawl: 'nạp truyện', scan: 'quét truyện', skeleton: 'dựng khung xương', split: 'chia tập', localize: 'bản địa hoá',
  write: 'viết kịch bản', season_reader: 'AI đọc cả mùa', visual: 'đề xuất nhận dạng', overview: 'tạo tổng quan truyện', other: 'xử lý kịch bản',
};
const FAIL_LABEL_CAP: Record<WorkbenchJobKind, string> = {
  crawl: 'Nạp truyện', scan: 'Quét', skeleton: 'Dựng khung xương', split: 'Chia tập', localize: 'Bản địa hoá',
  write: 'Viết kịch bản', season_reader: 'AI đọc cả mùa', visual: 'Đề xuất nhận dạng', overview: 'Tạo tổng quan truyện', other: 'Xử lý kịch bản',
};

/** Câu toast cho job vừa kết thúc ở vỏ. */
export const endedJobText = (j: EndedJob): { type: 'success' | 'error'; text: string } => {
  const gate = j.phase === GATE_JOB_PHASE || isRejudgeJob(j);   // «Chấm lại bằng AI»: cổng chia tập (③④) hoặc một tập (⑤)
  if (j.outcome === 'failed') {
    return { type: 'error', text: `${gate ? REJUDGE_LABEL : FAIL_LABEL_CAP[j.kind] ?? FAIL_LABEL_CAP.other} lỗi${j.note ? ` — ${j.note}` : ''}` };
  }
  return { type: 'success', text: gate ? 'Đã chấm lại xong.' : `Đã xong ${DONE_LABEL[j.kind] ?? DONE_LABEL.other}.` };
};
